import { SHIFT_ALERTS_MAX_GRACE_MINUTES, SHIFT_ALERTS_MAX_TEXT_MANAGERS } from '@order-up/models';
import { z } from 'zod';

export const AlertSeveritySchema = z.enum(['info', 'warning', 'urgent']);

export const SmsProviderTypeSchema = z.enum(['sns', 'twilio', 'sent']);

export const AlertRecipientWhenSchema = z.enum(['on_shift', 'always']);

/** Who receives a text alert — see `AlertRecipientRule` in @order-up/models. */
export const AlertRecipientRuleSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('role'),
      jobId: z.number().int().readonly(),
      when: AlertRecipientWhenSchema.readonly(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('employee'),
      employeeId: z.number().int().readonly(),
      when: AlertRecipientWhenSchema.readonly(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('phone'),
      name: z.string().max(100).readonly(),
      phone: z.string().min(10).max(20).readonly(),
    })
    .strict(),
]);

export const AlertPromptSchema = z
  .object({
    id: z.string().min(1).readonly(),
    message: z.string().min(1).max(500).readonly(),
    isDefault: z.boolean().readonly(),
    severity: AlertSeveritySchema.optional().readonly(),
    sortOrder: z.number().int().min(0).readonly(),
    // Absent = enabled; absent recipients = use the default list. Both are
    // optional so every prompt saved before them still parses.
    enabled: z.boolean().optional().readonly(),
    recipients: z.array(AlertRecipientRuleSchema).max(50).optional().readonly(),
  })
  .strict();

export const AlertRecipientSchema = z
  .object({
    name: z.string().min(1).max(100).readonly(),
    phone: z.string().min(10).max(20).readonly(),
    role: z.string().max(50).optional().readonly(),
  })
  .strict();

export const LocationAlertConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    // Optional: absent = the platform provider (see RestaurantAlertConfig).
    smsProvider: SmsProviderTypeSchema.optional().readonly(),
    alertPrompts: z.array(AlertPromptSchema).readonly(),
    recipients: z.array(AlertRecipientSchema).readonly(),
    defaultRecipients: z.array(AlertRecipientRuleSchema).max(50).optional().readonly(),
  })
  .strict();

/**
 * `settings.remoteApproval` — see `LocationRemoteApprovalConfig` in
 * @order-up/models. Read through `readRemoteApprovalConfig`, which drops a
 * phone rule and reads "enabled with no approvers" as off, so this accepts
 * what that reader tolerates rather than refusing a stored block.
 */
export const LocationRemoteApprovalConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    approvers: z.array(AlertRecipientRuleSchema).max(50).readonly(),
    includeOnShift: z.boolean().readonly(),
    sms: z.enum(['escalate', 'always', 'off']).readonly(),
  })
  .strict();

/**
 * An account uid as a chosen manager carries it (R56): not blank, at most 128
 * characters (Firebase's own bound), and never a `/` — it is a document id in
 * `shift_alert_subscribers/{uid}` and `user_{uid}` is its push topic. Blank
 * includes whitespace-only, which the reader (`readShiftAlertsConfig`) drops:
 * the schema refuses what the reader would not keep, so a save never stores a
 * uid the sweep reads as absent.
 */
const ShiftAlertManagerUidSchema = z
  .string()
  .min(1)
  .max(128)
  .refine((uid) => uid.trim().length > 0, { message: 'A uid cannot be blank' })
  .refine((uid) => !uid.includes('/'), { message: 'A uid cannot contain "/"' });

/**
 * `settings.shiftAlerts` — see `LocationShiftAlertsConfig` in @order-up/models.
 * Grace is 0..9, never 10 or more, so any late rung that fires is also a late
 * record (spec D17). The bound is the models constant the reader and the
 * settings screen use, so the three cannot disagree.
 *
 * `textManagerUids` (Ruling R56) is the one OPTIONAL key: the managers the
 * location chose to text. `PUT /shift-alerts/config` keeps the stored list
 * when a body leaves it out (a manager app built before R56 sends the block
 * without it, and must never wipe the list), so absent means "unchanged",
 * never "nobody". `requireTextConsent` (SOC 2 R40) is optional for the same
 * reason: absent keeps the stored switch, so a client built before it can
 * never switch the consent requirement off. Every other key stays required,
 * and an unknown key is still refused.
 */
export const LocationShiftAlertsConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    beforeShift: z.boolean().readonly(),
    late: z.boolean().readonly(),
    sms: z.enum(['escalate', 'off']).readonly(),
    graceMinutes: z.number().int().min(0).max(SHIFT_ALERTS_MAX_GRACE_MINUTES).readonly(),
    managerAlerts: z.boolean().readonly(),
    patternThreshold: z.number().int().min(1).nullable().readonly(),
    textManagerUids: z.array(ShiftAlertManagerUidSchema).max(SHIFT_ALERTS_MAX_TEXT_MANAGERS).optional().readonly(),
    requireTextConsent: z.boolean().optional().readonly(),
  })
  .strict();

/**
 * The body of `PUT /shift-alerts/subscription/me` — a manager's own choice of
 * how they receive a location's late and no-show alerts (spec §10.3a). See
 * `ShiftAlertSubscription` in @order-up/models. Every field is required: the
 * screen always sends the whole choice, and a partial body would leave the
 * stored one half old, half new.
 */
export const ShiftAlertSubscriptionSchema = z
  .object({
    mode: z.enum(['always', 'on_shift', 'off']).readonly(),
    push: z.boolean().readonly(),
    sms: z.boolean().readonly(),
  })
  .strict();

export type AlertRecipientRuleInput = z.infer<typeof AlertRecipientRuleSchema>;
export type AlertPromptInput = z.infer<typeof AlertPromptSchema>;
export type AlertRecipientInput = z.infer<typeof AlertRecipientSchema>;
export type LocationAlertConfigInput = z.infer<typeof LocationAlertConfigSchema>;
